<template>
	<div>
		<button
			:id="'button-list-box'"
			type="button"
			class="relative w-full pr-8 bg-transparent cursor-pointer text-sm"
			@click="openModal"
		>
			<span
				:class="{
					'block text-left': true
				}"
			>
				<span class="flex items-center">
					<template v-if="flags">
						<GlobeAltIcon
							v-if="localeProperties?.code === 'global'"
							class="w-[30px] h-[20px] mr-2 mt-[2px] float-left"
						/>
						<img
							v-else
							:src="getLocaleImage(getLanguageOption?.value)"
							alt="Flag"
							class="w-[30px] h-[20px] mr-2 mt-[2px] float-left"
						/>
					</template>
					{{ getLanguageOption?.label }}
				</span>
			</span>
			<span
				class="pointer-events-none absolute inset-y-0 right-0 flex items-center"
			>
				<ChevronDownIcon class="h-5 w-5 text-current hidden lg:block" />
				<ChevronRightIcon class="h-5 w-5 text-current lg:hidden" />
			</span>
		</button>
		<TransitionRoot appear :show="isOpen" as="template">
			<Dialog as="div" class="relative z-50" @close="closeModal">
				<TransitionChild
					as="template"
					enter="duration-300 ease-out"
					enter-from="opacity-0"
					enter-to="opacity-100"
					leave="duration-200 ease-in"
					leave-from="opacity-100"
					leave-to="opacity-0"
				>
					<div class="fixed inset-0"></div>
				</TransitionChild>
				<div class="fixed inset-0 lg:main-wrapper">
					<div
						class="h-full lg:h-auto lg:flex justify-end lg:p-4 text-right lg:mt-24 sm:max-w-sm lg:max-w-full"
					>
						<TransitionChild
							as="template"
							enter="duration-300 ease-out"
							enter-from="opacity-0 scale-95"
							enter-to="opacity-100 scale-100"
							leave="duration-200 ease-in"
							leave-from="opacity-100 scale-100"
							leave-to="opacity-0 scale-95"
						>
							<DialogPanel
								class="w-full lg:max-w-md overflow-hidden bg-white text-left align-middle shadow-xl transition-all relative flex flex-col h-full lg:h-auto lg:max-h-[80vh]"
							>
								<div class="mt-2">
									<div v-if="title2" class="p-6 border-b">
										{{ title1 }}
									</div>
									<XMarkIcon
										class="h-6 w-6 absolute top-8 right-6 cursor-pointer"
										@click="closeModal"
									/>
								</div>
								<div class="overflow-auto grow lg:grow-0 lg:max-h-[75vh]">
									<ul>
										<li
											v-for="item in options1"
											:key="getLanguageSelectionValue(item)"
											class="px-6 py-[15px] border-b hover:cursor-pointer"
											:class="{
												selected:
													valueLanguage === getLanguageSelectionValue(item)
											}"
											@click="selectLanguage(getLanguageSelectionValue(item))"
										>
											<span
												class="w-4 h-4 block float-left mr-2 border rounded-full mt-1"
												:class="{
													' border-4 ':
														valueLanguage === getLanguageSelectionValue(item)
												}"
											></span>
											<span>
												<template v-if="flags">
													<GlobeAltIcon
														v-if="item?.code === 'global'"
														class="w-[30px] h-[20px] mr-2 mt-[2px] float-left"
													/>
													<img
														v-else
														:src="getLocaleImage(item?.value)"
														alt="Flag"
														class="w-[30px] h-[20px] mr-2 mt-[2px] float-left"
													/>
												</template>
												{{ item.label }}
											</span>
										</li>
									</ul>
									<div class="p-6">
										<div v-if="title2">{{ title2 }}</div>
										<ul>
											<li
												v-for="item in options2"
												:key="item.Id"
												class="py-2 text-sm hover:cursor-pointer"
												:class="{ selected: valueCurrency === item.Id }"
												@click="selectCurrency(item.Id)"
											>
												<span>
													<span
														class="w-4 h-4 block float-left mr-2 border rounded-full mt-1"
														:class="{
															' border-4 ': valueCurrency === item.Id
														}"
													></span>
													{{ item.Code }}
												</span>
											</li>
										</ul>
									</div>
								</div>

								<div class="lg:mt-4 border-t lg:border-t-0 p-6">
									<button
										type="button"
										class="max-w w-full h-full button-primary"
										@click="handleUpdate"
									>
										{{ $t("Update") }}
									</button>
								</div>
								<div v-if="options3 && options3.length > 0" class="p-6">
									<div v-if="title3">{{ title3 }}</div>
									<ul>
										<li
											v-for="item in options3"
											:key="item.label"
											class="py-[15px] border-b hover:cursor-pointer"
											@click="switchDomain(item.value)"
										>
											<span
												class="w-4 h-4 block float-left mr-2 border rounded-full mt-1"
											></span>
											<span>
												{{ item.label }}
											</span>
										</li>
									</ul>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</TransitionRoot>
	</div>
</template>

<script setup lang="ts">
import {
	TransitionRoot,
	TransitionChild,
	Dialog,
	DialogPanel
} from "@headlessui/vue";
import {
	ChevronDownIcon,
	ChevronRightIcon,
	XMarkIcon,
	GlobeAltIcon
} from "@heroicons/vue/20/solid";
import type { Currency } from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type {
	CulturesNorce,
	AlternativesNorce
} from "~/types/components/header";
import { CULTURE_LABELS } from "~/utils/getCulturesFromLocales";

interface ElSelectExtendedType {
	modelValue?: ModelValue;
	flags?: boolean;
	title1?: string;
	title2?: string;
	title3?: string;
	options1: CulturesNorce[] | undefined;
	options2: Currency[] | undefined;
	options3?: AlternativesNorce[] | undefined;
	isTransparentHeader?: boolean;
}

interface ModelValue {
	language: string;
	currency: number;
}

const {
	modelValue,
	flags,
	title1,
	title2,
	title3,
	options1,
	options2,
	options3
} = defineProps<ElSelectExtendedType>();

const valueLanguage = ref<string | undefined>();
const valueCurrency = ref<number | undefined>(0);

const tempLanguage = ref<string | undefined>();
const tempCurrency = ref<number | undefined>(0);

const emit = defineEmits(["update:modelValue"]);

const isOpen = ref(false);

const { localeProperties } = useI18n();

function initailize() {
	valueLanguage.value = modelValue?.language;
	valueCurrency.value = modelValue?.currency;
	tempLanguage.value = valueLanguage.value;
	tempCurrency.value = valueCurrency.value;
}

function closeModal() {
	valueLanguage.value = tempLanguage.value;
	valueCurrency.value = tempCurrency.value;
	isOpen.value = false;
}

function openModal() {
	tempLanguage.value = valueLanguage.value;
	tempCurrency.value = valueCurrency.value;
	isOpen.value = true;
}

function selectLanguage(language: string) {
	valueLanguage.value = language;
}

function selectCurrency(currency: number) {
	valueCurrency.value = currency;
}

function handleUpdate() {
	emit("update:modelValue", {
		language: valueLanguage.value,
		currency: valueCurrency.value
	});
	isOpen.value = false;
}

const switchDomain = (targetDomain: string) => {
	const newUrl = `https://${targetDomain}`;

	return navigateTo(newUrl, {
		external: true
	});
};

const getLocaleImage = (locale: string) => `/images/flags/${locale}.svg`;

const getLanguageSelectionValue = (language: CulturesNorce) =>
	language.code ?? language.value;

const getLanguageOption = computed(() => {
	if (localeProperties.value?.code === "global") {
		return {
			label: CULTURE_LABELS[localeProperties.value.code],
			value: localeProperties.value.iso,
			code: localeProperties.value.code
		};
	}

	return options1?.find((language) => {
		return (
			getLanguageSelectionValue(language) === modelValue?.language ||
			language.value === modelValue?.language
		);
	});
});

initailize();

watch(
	() => modelValue,
	() => {
		initailize();
	},
	{ deep: true }
);
</script>
